import React from 'react';
import { Alert, Loading } from 'framework/ui/mui-builder';
import {
  TimelineContextData,
  TimelineEntity,
  TimelineItem,
  TimelineItemId,
  TimelinePagingHandler,
  TimelineRecordWrapper,
  useTimelineRegistryHelper,
} from 'framework/ui/timeline';
import { useListContext } from 'framework/list';
import { useShowContext } from 'framework/show';
import type { Data } from 'framework/types';
import { Step, StepLabel, Stepper, Typography } from '@mui/material';
import StepContent from '@mui/material/StepContent';
import Box from '@mui/material/Box';
import { getPersonTimelineIconSx } from './PersonTimelineRecord/personTimelineRecordSx';

const DEFAULT_FUNCTION = () => {};

interface FormConfig {
  icon: React.ReactNode;
  text: string;
}

export interface FormConfigMap {
  [key: string]: FormConfig;
  default: FormConfig;
}

interface PersonTimelineLayoutProps {
  noEntriesMessage: string;
  messageContext: string;
  formConfig?: FormConfigMap;
  getFormConfig?: (form: any) => FormConfig;
}

const PersonTimelineLayout = ({
  noEntriesMessage = 'No entries in this timeline yet.',
  messageContext,
  formConfig,
  getFormConfig = (form: any) => {
    if (!form || !formConfig) {
      return { icon: null, text: 'New entry' };
    }
    const formTypeName = form.type?.name || '';
    return formConfig[formTypeName] || formConfig.default;
  },
}: PersonTimelineLayoutProps) => {
  const {
    data: { items = [] } = {},
    registries,
    currentEditingId,
    setCurrentEditingId = DEFAULT_FUNCTION,
    updateInList = DEFAULT_FUNCTION,
    wrapItem = () => ({}),
    loading,
    addForm,
    isHighlighted = () => false,
  }: TimelineContextData & { data?: { items?: TimelineEntity[] } } = useListContext();
  const { getRegistry } = useTimelineRegistryHelper(registries || []);
  const { data: parentContext } = useShowContext();
  const { icon: formIcon, text: formText } = getFormConfig(addForm);

  const clearEditing = () => setCurrentEditingId(null);

  const renderUpdateForm = (
    UpdateFormTemplate: React.ReactElement,
    timelineItemEntity: TimelineEntity,
    source: string,
    hasFullData: boolean,
    additionalAfterUpdateSubmit?: (arg: TimelineItem, parentContext?: Data) => void,
  ) => (
    // @ts-ignore
    <UpdateFormTemplate
      messageContext={messageContext}
      entityId={timelineItemEntity?.id}
      afterSubmit={{
        execute: (id: TimelineItemId, data: TimelineItem) => {
          if (data) {
            updateInList(
              timelineItemEntity?.id || null,
              hasFullData
                ? data
                : wrapItem(
                    {
                      ...data,
                      timelineId: timelineItemEntity?.timelineId,
                    },
                    source,
                  ),
            );
            clearEditing();
          }
          if (additionalAfterUpdateSubmit) {
            additionalAfterUpdateSubmit(data, parentContext);
          }
        },
      }}
      key={`form-${timelineItemEntity?.id}`}
      getDataFunc={() => timelineItemEntity}
      onCancel={clearEditing}
    />
  );

  const renderLayout = (
    UpdateForm: React.ReactElement,
    RecordLayout: React.ReactElement,
    timelineItemEntity: TimelineEntity,
    source: string,
    hasFullData: boolean,
    additionalAfterUpdateSubmit?: (arg: TimelineItem, parentContext?: Data) => void,
    isPinned?: boolean,
  ) => (
    // @ts-ignore
    <RecordLayout
      updateComponent={renderUpdateForm(
        UpdateForm,
        timelineItemEntity,
        source,
        hasFullData,
        additionalAfterUpdateSubmit,
      )}
      isPinned={isPinned}
      isHighlighted={isHighlighted(timelineItemEntity?.timelineId || timelineItemEntity?.id)}
      isEditing={currentEditingId === timelineItemEntity?.id}
      onEdit={() => setCurrentEditingId(timelineItemEntity?.id || null)}
      key={`record-${timelineItemEntity?.id}`}
    />
  );

  if (loading) {
    return <Loading sx={{ position: 'initial' }} />;
  }

  const sortedItems = [...items]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .sort((a, b) => Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned)));

  return (
    <>
      {!items.length && !addForm && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {noEntriesMessage}
        </Alert>
      )}
      <div>
        <TimelinePagingHandler>
          <Stepper orientation="vertical" sx={{ '& .MuiStepConnector-line': { minHeight: 0 } }}>
            {addForm && (
              <Step key={`entry${items.length}`}>
                <StepLabel sx={{ padding: 0 }} icon={<Box sx={getPersonTimelineIconSx()}>{formIcon}</Box>}>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    sx={{
                      backgroundColor: 'background.primary',
                      pr: 1,
                      textAlign: 'right',
                    }}
                  >
                    {formText}
                  </Typography>
                </StepLabel>
                <StepContent sx={{ pt: '20px', pb: '30px' }}>{addForm}</StepContent>
              </Step>
            )}

            {sortedItems.map(item => {
              if (!item) return null;

              const {
                UpdateForm = <div />,
                source = '',
                RecordLayout = <div />,
                // @ts-ignore
                hasFullData = false,
                additionalAfterUpdateSubmit,
              } = getRegistry(item) || {};
              // @ts-ignore

              let timelineItemEntity;
              if (hasFullData) {
                timelineItemEntity = item;
              } else if (source && item[source]) {
                timelineItemEntity = item[source];
              } else {
                timelineItemEntity = item;
              }

              if (!timelineItemEntity || !timelineItemEntity.id) {
                return null;
              }

              const mergedTimelineId = {
                timelineId: item.id,
                ...timelineItemEntity,
              };
              const isPinned = item?.isPinned ?? timelineItemEntity?.isPinned;

              return (
                <React.Fragment key={timelineItemEntity.id}>
                  <TimelineRecordWrapper entity={mergedTimelineId || {}} key={`wrapper-${timelineItemEntity.id}`}>
                    {renderLayout(
                      UpdateForm,
                      RecordLayout,
                      mergedTimelineId,
                      source,
                      hasFullData,
                      additionalAfterUpdateSubmit,
                      isPinned,
                    )}
                  </TimelineRecordWrapper>
                </React.Fragment>
              );
            })}
          </Stepper>
        </TimelinePagingHandler>
      </div>
    </>
  );
};

export default PersonTimelineLayout;
